import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createComment as apiCreateComment,
  deleteComment as apiDeleteComment,
  listComments as apiListComments,
  listReplies as apiListReplies,
  postReply as apiPostReply,
  updateStatus as apiUpdateStatus,
  type CreatePayload,
} from './api';
import type { CommentResponse, Draft, Reply } from './types';

// localStorage keys.
const LS_AUTHOR = 'mock-ui-comment-author';

type CommentContextValue = {
  version: string;

  commentMode: boolean;
  toggleCommentMode: () => void;

  sidebarOpen: boolean;
  toggleSidebar: () => void;
  openSidebar: () => void;

  username: string | null;
  setUsername: (name: string) => void;

  /** Comments scoped to the current version. Used by PinLayer + overlay. */
  comments: CommentResponse[];
  /** Comments across every registered version. Used by the sidebar so
   *  reviewers can see and jump across the full audit trail. */
  allComments: CommentResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  draft: Draft | null;
  startDraft: (d: Draft) => void;
  cancelDraft: () => void;
  submitDraft: (body: string, screenshotUrl?: string) => Promise<void>;

  openThreadNumber: number | null;
  openThread: (n: number) => void;
  closeThread: () => void;

  fetchReplies: (n: number) => Promise<Reply[]>;
  postReply: (n: number, body: string) => Promise<void>;
  resolve: (n: number) => Promise<void>;
  reopen: (n: number) => Promise<void>;
  deleteComment: (n: number) => Promise<void>;
};

const CommentContext = createContext<CommentContextValue | null>(null);

export function useComments(): CommentContextValue {
  const ctx = useContext(CommentContext);
  if (!ctx) throw new Error('useComments must be used inside <CommentProvider>');
  return ctx;
}

export function CommentProvider({
  version,
  children,
}: {
  version: string;
  children: ReactNode;
}) {
  const [commentMode, setCommentMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [username, setUsernameState] = useState<string | null>(() => {
    return typeof window !== 'undefined' ? window.localStorage.getItem(LS_AUTHOR) : null;
  });

  const [allComments, setAllComments] = useState<CommentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current-version slice, derived from the all-versions fetch so the sidebar
  // and the pin layer share a single source of truth and a single round trip.
  const comments = useMemo(
    () => allComments.filter((c) => c.version === version),
    [allComments, version],
  );

  const [draft, setDraft] = useState<Draft | null>(null);
  const [openThreadNumber, setOpenThreadNumber] = useState<number | null>(null);

  const setUsername = useCallback((name: string) => {
    const trimmed = name.trim().slice(0, 60);
    if (!trimmed) return;
    window.localStorage.setItem(LS_AUTHOR, trimmed);
    setUsernameState(trimmed);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch every version's comments so the sidebar can show the full
      // audit trail grouped by version. The pin layer reads the
      // current-version slice via the derived `comments` memo above.
      const list = await apiListComments('all');
      setAllComments(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load. No version dep — refresh always fetches all versions.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggleCommentMode = useCallback(() => {
    setCommentMode((m) => {
      const next = !m;
      // Closing comment mode dismisses any in-progress draft.
      if (!next) setDraft(null);
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => setSidebarOpen((o) => !o), []);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);

  const startDraft = useCallback((d: Draft) => {
    setDraft(d);
    setOpenThreadNumber(null);
  }, []);

  const cancelDraft = useCallback(() => setDraft(null), []);

  const submitDraft = useCallback(
    async (body: string, screenshotUrl?: string) => {
      if (!draft || !username) return;
      const payload: CreatePayload = {
        version,
        component: draft.component,
        route: window.location.pathname,
        selector: draft.selector,
        elementTag: draft.elementTag,
        elementText: draft.elementText,
        elementAriaLabel: draft.elementAriaLabel ?? null,
        offsetX: draft.offsetX,
        offsetY: draft.offsetY,
        viewport: draft.viewport,
        author: username,
        body,
        screenshotUrl,
      };
      const created = await apiCreateComment(payload);
      // Prepend the just-created comment to local state instead of refetching.
      // GitHub's labeled-issue list API has indexing latency, so an immediate
      // refresh often misses the new issue and the pin marker won't appear
      // until the next page load. The derived current-version slice picks it
      // up automatically via the memo above.
      setAllComments((prev) => [created, ...prev]);
      setDraft(null);
      // Stay in comment mode so the new pin is visible alongside the existing
      // ones. The popover closes (setDraft above); the reviewer can drop
      // another pin or toggle comment mode off manually when they're done.
    },
    [draft, username, version],
  );

  const openThread = useCallback((n: number) => {
    setOpenThreadNumber(n);
    setDraft(null);
  }, []);

  const closeThread = useCallback(() => setOpenThreadNumber(null), []);

  const fetchReplies = useCallback(async (n: number) => {
    return await apiListReplies(n);
  }, []);

  const postReply = useCallback(
    async (n: number, body: string) => {
      if (!username) return;
      await apiPostReply(n, username, body);
      await refresh();
    },
    [refresh, username],
  );

  const resolve = useCallback(
    async (n: number) => {
      await apiUpdateStatus(n, 'resolved');
      setOpenThreadNumber(null);
      await refresh();
    },
    [refresh],
  );

  const reopen = useCallback(
    async (n: number) => {
      await apiUpdateStatus(n, 'reopen');
      await refresh();
    },
    [refresh],
  );

  const deleteComment = useCallback(
    async (n: number) => {
      if (!username) return;
      await apiDeleteComment(n, username);
      setOpenThreadNumber(null);
      await refresh();
    },
    [refresh, username],
  );

  const value = useMemo<CommentContextValue>(
    () => ({
      version,
      commentMode,
      toggleCommentMode,
      sidebarOpen,
      toggleSidebar,
      openSidebar,
      username,
      setUsername,
      comments,
      allComments,
      loading,
      error,
      refresh,
      draft,
      startDraft,
      cancelDraft,
      submitDraft,
      openThreadNumber,
      openThread,
      closeThread,
      fetchReplies,
      postReply,
      resolve,
      reopen,
      deleteComment,
    }),
    [
      version,
      commentMode,
      toggleCommentMode,
      sidebarOpen,
      toggleSidebar,
      openSidebar,
      username,
      setUsername,
      comments,
      allComments,
      loading,
      error,
      refresh,
      draft,
      startDraft,
      cancelDraft,
      submitDraft,
      openThreadNumber,
      openThread,
      closeThread,
      fetchReplies,
      postReply,
      resolve,
      reopen,
      deleteComment,
    ],
  );

  return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
}
